import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Image, Upload, Trash2, Loader2, Check, X, Star,
  AlertCircle, RefreshCw, Plus, Instagram
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

export default function AdminHeroImages() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [editingImage, setEditingImage] = useState(null);
  const [editHandles, setEditHandles] = useState({ instagram: [], tiktok: [] });
  const [newInstagramHandle, setNewInstagramHandle] = useState('');
  const [newTiktokHandle, setNewTiktokHandle] = useState('');
  const [savingHandles, setSavingHandles] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/admin/hero-images`);
      setImages(response.data.images);
    } catch (error) {
      console.error('Failed to fetch hero images:', error);
      toast.error('Failed to load hero images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF images.');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned upload URL
      const extension = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const uploadUrlResponse = await axios.post(
        `${API}/admin/hero-images/upload`,
        null,
        {
          params: {
            content_type: file.type,
            file_extension: extension
          }
        }
      );

      const { upload_url, file_path } = uploadUrlResponse.data;
      setUploadProgress(25);

      // Step 2: Upload directly to R2
      // IMPORTANT: Remove Authorization header - presigned URLs include auth in the URL itself
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type,
        },
        // Don't send auth headers to R2 - they interfere with presigned URL signature
        transformRequest: [(data, headers) => {
          delete headers.Authorization;
          delete headers.common;
          return data;
        }],
        onUploadProgress: (progressEvent) => {
          const percentCompleted = 25 + Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      });

      setUploadProgress(80);

      // Step 3: Confirm upload in database
      const confirmResponse = await axios.post(`${API}/admin/hero-images`, {
        file_path: file_path,
        caption: '',
        is_default_fallback: false,
        instagram_handles: [],
        tiktok_handles: []
      });

      setUploadProgress(100);
      toast.success('Hero image uploaded successfully!');
      
      // Add new image to list
      setImages(prev => [confirmResponse.data, ...prev]);

    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const toggleActive = async (imageId, currentActive) => {
    try {
      await axios.put(`${API}/admin/hero-images/${imageId}/toggle?active=${!currentActive}`);
      setImages(prev => prev.map(img => 
        img.id === imageId ? { ...img, active: !currentActive } : img
      ));
      toast.success(currentActive ? 'Image disabled' : 'Image enabled');
    } catch (error) {
      toast.error('Failed to update image status');
    }
  };

  const setAsFallback = async (imageId) => {
    try {
      await axios.put(`${API}/admin/hero-images/${imageId}/set-fallback`);
      setImages(prev => prev.map(img => ({
        ...img,
        is_default_fallback: img.id === imageId
      })));
      toast.success('Set as default fallback image');
    } catch (error) {
      toast.error('Failed to set fallback image');
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this hero image?')) return;

    try {
      await axios.delete(`${API}/admin/hero-images/${imageId}`);
      setImages(prev => prev.filter(img => img.id !== imageId));
      toast.success('Hero image deleted');
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  // Social handles editing
  const openHandlesEditor = (image) => {
    setEditingImage(image);
    setEditHandles({
      instagram: image.instagram_handles || [],
      tiktok: image.tiktok_handles || []
    });
    setNewInstagramHandle('');
    setNewTiktokHandle('');
  };

  const closeHandlesEditor = () => {
    setEditingImage(null);
    setEditHandles({ instagram: [], tiktok: [] });
    setNewInstagramHandle('');
    setNewTiktokHandle('');
  };

  const addInstagramHandle = () => {
    const handle = newInstagramHandle.trim();
    if (!handle) return;
    // Add @ if not present
    const formatted = handle.startsWith('@') ? handle : `@${handle}`;
    if (!editHandles.instagram.includes(formatted)) {
      setEditHandles(prev => ({
        ...prev,
        instagram: [...prev.instagram, formatted]
      }));
    }
    setNewInstagramHandle('');
  };

  const addTiktokHandle = () => {
    const handle = newTiktokHandle.trim();
    if (!handle) return;
    // Add @ if not present
    const formatted = handle.startsWith('@') ? handle : `@${handle}`;
    if (!editHandles.tiktok.includes(formatted)) {
      setEditHandles(prev => ({
        ...prev,
        tiktok: [...prev.tiktok, formatted]
      }));
    }
    setNewTiktokHandle('');
  };

  const removeInstagramHandle = (handle) => {
    setEditHandles(prev => ({
      ...prev,
      instagram: prev.instagram.filter(h => h !== handle)
    }));
  };

  const removeTiktokHandle = (handle) => {
    setEditHandles(prev => ({
      ...prev,
      tiktok: prev.tiktok.filter(h => h !== handle)
    }));
  };

  const saveHandles = async () => {
    if (!editingImage) return;
    
    setSavingHandles(true);
    try {
      await axios.put(`${API}/admin/hero-images/${editingImage.id}/socials`, {
        instagram_handles: editHandles.instagram,
        tiktok_handles: editHandles.tiktok
      });
      
      // Update local state
      setImages(prev => prev.map(img => 
        img.id === editingImage.id 
          ? { ...img, instagram_handles: editHandles.instagram, tiktok_handles: editHandles.tiktok }
          : img
      ));
      
      toast.success('Social handles saved!');
      closeHandlesEditor();
    } catch (error) {
      console.error('Failed to save handles:', error);
      toast.error('Failed to save social handles');
    } finally {
      setSavingHandles(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Hero Images</h1>
          <p className="text-neutral-400 mt-1">
            Manage the background images shown on the landing page hero section.
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={fetchImages}
            className="border-white/20 text-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading {uploadProgress}%
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Image
              </>
            )}
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* Upload Progress Bar */}
      {uploading && (
        <div className="bg-[#121212] rounded-xl border border-white/10 p-4">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="w-5 h-5 animate-spin text-red-500" />
            <span className="text-white">Uploading image...</span>
          </div>
          <div className="w-full bg-neutral-800 rounded-full h-2">
            <div 
              className="bg-red-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-200">
          <p className="font-medium mb-1">How Hero Images Work</p>
          <ul className="list-disc list-inside space-y-1 text-blue-300">
            <li>Active images are randomly shown on the landing page on each page load</li>
            <li>Set one image as "Default Fallback" - it will be used if no other images load</li>
            <li>Disabled images won't appear on the landing page</li>
            <li>Add Instagram/TikTok handles to credit photographers or car owners</li>
            <li>Recommended size: 1920x1080 or larger (16:9 aspect ratio)</li>
          </ul>
        </div>
      </div>

      {/* Social Handles Editor Modal */}
      {editingImage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-2xl border border-white/10 w-full max-w-lg p-6">
            <h3 className="text-xl font-bold text-white mb-4">Edit Social Handles</h3>
            <p className="text-neutral-400 text-sm mb-6">
              Add Instagram and TikTok handles for the photographer, car owner, or anyone featured in this image.
            </p>
            
            {/* Instagram Handles */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-white font-medium mb-3">
                <Instagram className="w-4 h-4 text-pink-500" />
                Instagram Handles
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newInstagramHandle}
                  onChange={(e) => setNewInstagramHandle(e.target.value)}
                  placeholder="@username"
                  className="bg-neutral-800 border-white/10 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addInstagramHandle())}
                />
                <Button
                  type="button"
                  onClick={addInstagramHandle}
                  className="bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 border-pink-500/30"
                  variant="outline"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {editHandles.instagram.map((handle, idx) => (
                  <Badge 
                    key={idx} 
                    className="bg-pink-500/20 text-pink-300 border-pink-500/30 flex items-center gap-1 px-3 py-1"
                  >
                    <Instagram className="w-3 h-3" />
                    {handle}
                    <button 
                      onClick={() => removeInstagramHandle(handle)}
                      className="ml-1 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {editHandles.instagram.length === 0 && (
                  <span className="text-neutral-500 text-sm">No handles added</span>
                )}
              </div>
            </div>
            
            {/* TikTok Handles */}
            <div className="mb-6">
              <label className="flex items-center gap-2 text-white font-medium mb-3">
                <TikTokIcon className="w-4 h-4 text-cyan-400" />
                TikTok Handles
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newTiktokHandle}
                  onChange={(e) => setNewTiktokHandle(e.target.value)}
                  placeholder="@username"
                  className="bg-neutral-800 border-white/10 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTiktokHandle())}
                />
                <Button
                  type="button"
                  onClick={addTiktokHandle}
                  className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border-cyan-500/30"
                  variant="outline"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {editHandles.tiktok.map((handle, idx) => (
                  <Badge 
                    key={idx} 
                    className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 flex items-center gap-1 px-3 py-1"
                  >
                    <TikTokIcon className="w-3 h-3" />
                    {handle}
                    <button 
                      onClick={() => removeTiktokHandle(handle)}
                      className="ml-1 hover:text-white"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {editHandles.tiktok.length === 0 && (
                  <span className="text-neutral-500 text-sm">No handles added</span>
                )}
              </div>
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={closeHandlesEditor}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={saveHandles}
                disabled={savingHandles}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {savingHandles ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Handles'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Images Grid */}
      {images.length === 0 ? (
        <div className="text-center py-16 bg-[#121212] rounded-2xl border border-white/10">
          <Image className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">No Hero Images</h3>
          <p className="text-neutral-400 mb-6">
            Upload images to display on the landing page hero section.
          </p>
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload First Image
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((image) => (
            <div 
              key={image.id}
              className={`bg-[#121212] rounded-2xl border overflow-hidden transition-all ${
                image.active 
                  ? 'border-green-500/30' 
                  : 'border-white/10 opacity-60'
              }`}
            >
              {/* Image Preview */}
              <div className="relative aspect-video bg-neutral-900">
                {image.presigned_url ? (
                  <img
                    src={image.presigned_url}
                    alt={image.caption || 'Hero image'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x225?text=Image+Error';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Image className="w-12 h-12 text-neutral-700" />
                  </div>
                )}
                
                {/* Badges */}
                <div className="absolute top-2 left-2 flex gap-2">
                  {image.is_default_fallback && (
                    <Badge className="bg-yellow-500/90 text-black">
                      <Star className="w-3 h-3 mr-1" />
                      Fallback
                    </Badge>
                  )}
                  <Badge className={image.active 
                    ? 'bg-green-500/90 text-white' 
                    : 'bg-neutral-700/90 text-neutral-300'
                  }>
                    {image.active ? 'Active' : 'Disabled'}
                  </Badge>
                </div>
                
                {/* Social handles preview */}
                {((image.instagram_handles && image.instagram_handles.length > 0) || 
                  (image.tiktok_handles && image.tiktok_handles.length > 0)) && (
                  <div className="absolute top-2 right-2 flex gap-1">
                    {image.instagram_handles?.length > 0 && (
                      <Badge className="bg-pink-500/90 text-white">
                        <Instagram className="w-3 h-3 mr-1" />
                        {image.instagram_handles.length}
                      </Badge>
                    )}
                    {image.tiktok_handles?.length > 0 && (
                      <Badge className="bg-cyan-500/90 text-white">
                        <TikTokIcon className="w-3 h-3 mr-1" />
                        {image.tiktok_handles.length}
                      </Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="p-4">
                <p className="text-xs text-neutral-500 mb-3 truncate">
                  {image.file_path}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => toggleActive(image.id, image.active)}
                    className={`flex-1 border-white/20 ${
                      image.active ? 'text-red-400' : 'text-green-400'
                    }`}
                  >
                    {image.active ? (
                      <>
                        <X className="w-4 h-4 mr-1" />
                        Disable
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Enable
                      </>
                    )}
                  </Button>
                  
                  {!image.is_default_fallback && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAsFallback(image.id)}
                      className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                      title="Set as default fallback"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openHandlesEditor(image)}
                    className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                    title="Edit social handles"
                  >
                    <Instagram className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteImage(image.id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    title="Delete image"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
